import createDebug from 'debug';
import { Loader2, Share2, Trash2, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { getShares, type ShareInfo, shareLink, unshareLink, updateShareRole } from '@/api/sharing';
import type { User } from '@/api/users';
import { listUsers } from '@/api/users';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';

const debug = createDebug('app:share-dialog');

interface ShareLinkDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	linkId: number;
	linkTitle: string;
}

export function ShareLinkDialog({ open, onOpenChange, linkId, linkTitle }: ShareLinkDialogProps) {
	const [users, setUsers] = useState<User[]>([]);
	const [shares, setShares] = useState<ShareInfo[]>([]);
	const [loading, setLoading] = useState(false);
	const [sharesLoading, setSharesLoading] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
	const [selectedRole, setSelectedRole] = useState<'editor' | 'viewer'>('viewer');
	const [error, setError] = useState<string | null>(null);

	// Load users and shares when dialog opens
	useEffect(() => {
		if (!open) return;

		async function loadData() {
			setLoading(true);
			setSharesLoading(true);
			setError(null);

			try {
				// Load users and shares in parallel
				const [usersData, sharesData] = await Promise.all([
					listUsers({ is_active: true }),
					getShares(linkId),
				]);

				setUsers(usersData.users);
				setShares(sharesData);
			} catch (err) {
				debug('Failed to load data: %o', err);
				setError(err instanceof Error ? err.message : 'Failed to load data');
			} finally {
				setLoading(false);
				setSharesLoading(false);
			}
		}

		loadData();
	}, [open, linkId]);

	const handleShare = useCallback(async () => {
		if (!selectedUserId) return;

		setLoading(true);
		setError(null);

		try {
			await shareLink(linkId, selectedUserId, selectedRole);

			// Reload shares to get updated list
			const updatedShares = await getShares(linkId);
			setShares(updatedShares);

			// Reset selection
			setSelectedUserId(null);
			setSelectedRole('viewer');
		} catch (err) {
			debug('Failed to share link: %o', err);
			setError(err instanceof Error ? err.message : 'Failed to share link');
		} finally {
			setLoading(false);
		}
	}, [linkId, selectedUserId, selectedRole]);

	const handleRevoke = useCallback(
		async (userId: number) => {
			setLoading(true);
			setError(null);

			try {
				await unshareLink(linkId, userId);

				// Reload shares to get updated list
				const updatedShares = await getShares(linkId);
				setShares(updatedShares);
			} catch (err) {
				debug('Failed to revoke share: %o', err);
				setError(err instanceof Error ? err.message : 'Failed to revoke share');
			} finally {
				setLoading(false);
			}
		},
		[linkId]
	);

	const handleRoleChange = useCallback(
		async (userId: number, newRole: 'editor' | 'viewer') => {
			setLoading(true);
			setError(null);

			try {
				await updateShareRole(linkId, userId, newRole);

				// Update local state optimistically
				setShares((prev) =>
					prev.map((share) => (share.user_id === userId ? { ...share, role: newRole } : share))
				);
			} catch (err) {
				debug('Failed to update role: %o', err);
				setError(err instanceof Error ? err.message : 'Failed to update role');

				// Reload shares on error to ensure consistency
				const updatedShares = await getShares(linkId);
				setShares(updatedShares);
			} finally {
				setLoading(false);
			}
		},
		[linkId]
	);

	// Filter out users who already have access
	const availableUsers = users.filter((user) => !shares.some((share) => share.user_id === user.id));

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Share2 className="size-5" />
						Share Link
					</DialogTitle>
					<DialogDescription className="line-clamp-1">{linkTitle}</DialogDescription>
				</DialogHeader>

				{error && (
					<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
						{error}
					</div>
				)}

				{/* Share with new user */}
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<Users className="size-4 text-muted-foreground" />
						<span className="text-sm font-medium">Share with</span>
					</div>

					<div className="flex gap-2">
						<Select
							value={selectedUserId?.toString() ?? ''}
							onValueChange={(value) => setSelectedUserId(Number(value))}
							disabled={loading || availableUsers.length === 0}
						>
							<SelectTrigger className="flex-1">
								<SelectValue
									placeholder={
										availableUsers.length === 0 ? 'No users available' : 'Select a user...'
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{availableUsers.map((user) => (
									<SelectItem key={user.id} value={user.id.toString()}>
										<div className="flex flex-col items-start">
											<span className="font-medium">{user.display_name || user.username}</span>
											{user.display_name && (
												<span className="text-xs text-muted-foreground">@{user.username}</span>
											)}
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>

						<Select
							value={selectedRole}
							onValueChange={(value) => setSelectedRole(value as 'editor' | 'viewer')}
							disabled={loading}
						>
							<SelectTrigger className="w-32">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="viewer">Viewer</SelectItem>
								<SelectItem value="editor">Editor</SelectItem>
							</SelectContent>
						</Select>

						<Button
							onClick={handleShare}
							disabled={loading || !selectedUserId}
							size="sm"
							className="shrink-0"
						>
							{loading ? <Loader2 className="size-4 animate-spin" /> : 'Share'}
						</Button>
					</div>
				</div>

				{/* Existing shares list */}
				<div className="space-y-3">
					<div className="flex items-center justify-between">
						<span className="text-sm font-medium">Shared with ({shares.length})</span>
					</div>

					{sharesLoading ? (
						<div className="flex items-center justify-center py-8">
							<Loader2 className="size-6 animate-spin text-muted-foreground" />
						</div>
					) : shares.length === 0 ? (
						<div className="text-center py-8 text-sm text-muted-foreground">
							Not shared with anyone yet
						</div>
					) : (
						<div className="space-y-2 max-h-64 overflow-y-auto">
							{shares.map((share) => (
								<div
									key={share.user_id}
									className="flex items-center gap-2 p-2 rounded-md border bg-muted/30"
								>
									<div className="flex-1 min-w-0">
										<div className="font-medium truncate">
											{share.display_name || share.username}
										</div>
										{share.display_name && (
											<div className="text-xs text-muted-foreground truncate">
												@{share.username}
											</div>
										)}
									</div>

									<Select
										value={share.role}
										onValueChange={(value) =>
											handleRoleChange(share.user_id, value as 'editor' | 'viewer')
										}
										disabled={loading}
									>
										<SelectTrigger className="w-28" size="sm">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="viewer">Viewer</SelectItem>
											<SelectItem value="editor">Editor</SelectItem>
										</SelectContent>
									</Select>

									<Button
										variant="ghost"
										size="icon"
										className="size-8 shrink-0"
										onClick={() => handleRevoke(share.user_id)}
										disabled={loading}
										title="Revoke access"
									>
										<Trash2 className="size-4 text-destructive" />
									</Button>
								</div>
							))}
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
